import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    IconButton,
    Stack,
    Typography,
    Pagination,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { welderQualificationReleaseService } from '../../../services/welderQualificationReleaseService';
import {
    DOCUMENTS_ITEMS,
    FIELD_VALIDATION_ITEMS,
    PRODUCTION_CONTROL_ITEMS,
    TOTAL_CHECKS,
    countChecked
} from './welderQualificationReleaseSchema';

const WelderQualificationReleaseList = () => {
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        loadForms();
    }, []);

    const loadForms = async () => {
        setLoading(true);
        try {
            const data = await welderQualificationReleaseService.getAllFromIndexedDB();
            // Отправленные формы из списка убираем — они уже на сервере.
            const sorted = (data || [])
                .filter(f => f.status !== 'submitted')
                .sort((a, b) => {
                    const dateA = new Date(a.updatedAt || a.createdAt || 0);
                    const dateB = new Date(b.updatedAt || b.createdAt || 0);
                    return dateB - dateA;
                });
            setForms(sorted);
        } catch (error) {
            console.error('Error loading forms:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (uuid) => {
        const result = await Swal.fire({
            title: 'Delete this form?',
            text: 'This draft will be removed from this device.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it'
        });

        if (!result.isConfirmed) return;

        try {
            await welderQualificationReleaseService.deleteFromIndexedDB(uuid);
            loadForms();
        } catch (error) {
            console.error('Error deleting form:', error);
            Swal.fire('Error', 'Failed to delete form', 'error');
        }
    };

    const paginatedForms = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return forms.slice(startIndex, startIndex + itemsPerPage);
    }, [forms, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(forms.length / itemsPerPage);

    const handlePageChange = (event, value) => setCurrentPage(value);

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const cardTitle = (rec) => {
        if (rec.welderName) return rec.welderName;
        const job = rec.jobCode || '';
        const jobName = rec.jobName || '';
        if (job || jobName) return `${job}${job && jobName ? ' - ' : ''}${jobName}`;
        return rec.uuid;
    };

    const progressOf = (rec) =>
        countChecked(rec.documentsChecklist, DOCUMENTS_ITEMS) +
        countChecked(rec.fieldValidationChecklist, FIELD_VALIDATION_ITEMS) +
        countChecked(rec.productionControls, PRODUCTION_CONTROL_ITEMS);

    const formatDate = (dateStr) => (dateStr ? dayjs(dateStr).format('MM/DD/YYYY') : '—');

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Welder Qualification &amp; Release</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/operations/welder-qualification-release/form')}
                        sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                    >
                        New Form
                    </Button>
                </Box>
            </Box>

            {/* List */}
            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}>
                        <Typography>Loading...</Typography>
                    </Card>
                ) : forms.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No forms found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Form" to create one
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedForms.map((rec) => {
                                const done = progressOf(rec);
                                return (
                                    <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
                                                        {cardTitle(rec)}
                                                    </Typography>
                                                    <Chip
                                                        label="Draft"
                                                        color="warning"
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                    <Chip
                                                        label={`${done}/${TOTAL_CHECKS} checks`}
                                                        color={done === TOTAL_CHECKS ? 'success' : 'default'}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                </Box>
                                                <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Job: {rec.jobCode || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Date: {formatDate(rec.releaseDate)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        WPS: {rec.wpsNo || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Updated: {dayjs(rec.updatedAt || rec.createdAt).format('MM/DD/YYYY HH:mm')}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => navigate(`/operations/welder-qualification-release/form?uuid=${encodeURIComponent(rec.uuid)}`)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(rec.uuid)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Stack>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                                        <MenuItem value={5}>5 per page</MenuItem>
                                        <MenuItem value={10}>10 per page</MenuItem>
                                        <MenuItem value={25}>25 per page</MenuItem>
                                        <MenuItem value={50}>50 per page</MenuItem>
                                    </Select>
                                </FormControl>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default WelderQualificationReleaseList;
